import {
  Badge,
  Box,
  Checkbox,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FiAward } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {
  AwardMethodDto,
  BidV2Status,
  type EvalBidDto,
  type EvaluationDto,
  type EventValueDto,
} from "../../../../autogen/bff-api";
import { MinimumRequirementOutcomeBadge } from "../minimum-requirements/MinimumRequirementOutcomeBadge";

const formatPrice = (v?: EventValueDto | null): string =>
  v?.value != null ? `${v.value.toLocaleString("nb-NO")} ${v.currency}` : "–";

type AwardBidsTableProps = {
  evaluation: EvaluationDto;
  selectedBidIds: string[];
} & ({ mode: "select"; onToggle: (bidId: string) => void } | { mode: "confirmed"; onToggle?: never });

const sortAwardBids = (bids: EvalBidDto[]) =>
  [...bids].sort((a, b) => {
    const aRejected = a.status === BidV2Status.Rejected || a.rank <= 0;
    const bRejected = b.status === BidV2Status.Rejected || b.rank <= 0;
    if (aRejected !== bRejected) return aRejected ? 1 : -1;
    return a.rank - b.rank;
  });

export const AwardBidsTable = ({ evaluation, mode, selectedBidIds, onToggle }: AwardBidsTableProps) => {
  const { t } = useTranslation();
  const isLowestPrice = evaluation.awardMethod === AwardMethodDto.LowestPrice;
  const bids = sortAwardBids(evaluation.bids);

  const table = (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th>{mode === "select" ? t("Select") : t("Winner")}</Th>
          <Th textAlign="center">#</Th>
          <Th>{t("Supplier")}</Th>
          {isLowestPrice ? <Th isNumeric>{t("Bid sum")}</Th> : <Th isNumeric>{t("Total score")}</Th>}
        </Tr>
      </Thead>
      <Tbody>
        {bids.map((bid) => {
          const isRejected =
            bid.status === BidV2Status.Rejected || bid.rank <= 0;
          const isWinner = selectedBidIds.includes(bid.bidId);
          return (
            <Tr
              key={bid.bidId}
              bg={isRejected ? "red.50" : isWinner ? "green.50" : undefined}
            >
              <Td
                borderLeft={isRejected || isWinner ? "3px solid" : undefined}
                borderLeftColor={
                  isRejected ? "red.300" : isWinner ? "green.400" : undefined
                }
              >
                {mode === "select" ? (
                  <Checkbox
                    isChecked={isWinner}
                    onChange={() => onToggle(bid.bidId)}
                    colorScheme="teal"
                    isDisabled={isRejected}
                    aria-label={t("Award to {{supplier}}", { supplier: bid.supplierName || t("Unknown") }) ?? undefined}
                  />
                ) : isWinner ? (
                  <Box
                    as={FiAward}
                    color="green.500"
                    boxSize="5"
                    data-testid="award-winner-icon"
                    aria-label={t("Winner") ?? undefined}
                  />
                ) : null}
              </Td>
              <Td textAlign="center">
                <Flex
                  align="center"
                  justify="center"
                  display="inline-flex"
                  boxSize="7"
                  rounded="full"
                  bg={
                    isRejected
                      ? "red.100"
                      : isWinner
                        ? "smPrimary"
                        : "smBackgroundSecondary"
                  }
                  color={
                    isRejected ? "red.700" : isWinner ? "white" : "smMuted"
                  }
                  fontWeight="bold"
                  fontSize="sm"
                >
                  {isRejected ? "–" : bid.rank}
                </Flex>
              </Td>
              <Td>
                <Flex align="center" gap="2" wrap="wrap">
                  <Text fontWeight="semibold" color="smText">
                    {bid.supplierName || t("Unknown")}
                  </Text>
                  {isRejected && (
                    <Badge colorScheme="red">{t("Rejected")}</Badge>
                  )}
                  {!isRejected && (
                    <MinimumRequirementOutcomeBadge
                      minimumRequirements={bid.minimumRequirements}
                    />
                  )}
                </Flex>
                {!isLowestPrice && (
                  <Text fontSize="sm" color="smMuted">
                    {formatPrice(bid.effectivePrice ?? bid.price)}
                  </Text>
                )}
              </Td>
              {isLowestPrice ? (
                <Td isNumeric fontWeight="medium" color="smText">
                  {formatPrice(bid.effectivePrice ?? bid.price)}
                </Td>
              ) : (
                <Td
                  isNumeric
                  fontWeight="bold"
                  fontFamily="mono"
                  color={isWinner ? "smPrimary" : "smText"}
                >
                  {bid.weightedTotal.toFixed(1)}
                </Td>
              )}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );

  return (
    <Box
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      overflow="hidden"
    >
      <TableContainer>{table}</TableContainer>
    </Box>
  );
};
